import React, { PropsWithChildren, useState } from "react";
import { createPortal } from "react-dom";

import { autoUpdate, Placement } from "@floating-ui/react";
import { flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { CSSProperties } from "styled-components";

import * as Styled from "./Tooltip.styles";

export interface TooltipProps {
  text: string;
  position?: Placement;
  offset?: number;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  prewrap?: boolean;
  portal?: boolean;
  contentStyle?: CSSProperties;
}

export default function Tooltip({
  children,
  text,
  position = "right",
  offset: offsetProp = 1,
  disabled = false,
  className,
  style = {},
  prewrap,
  portal = false,
  contentStyle,
}: PropsWithChildren<TooltipProps>) {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles } = useFloating({
    placement: position,
    strategy: portal ? "fixed" : "absolute",
    middleware: [
      offset(offsetProp * 10), // assuming 1rem = 16px
      flip(),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const content = (
    <Styled.TooltipContent
      ref={refs.setFloating}
      $prewrap={prewrap}
      style={{
        ...floatingStyles,
        ...contentStyle,
        visibility: open ? "visible" : "hidden",
      }}
    >
      {text}
    </Styled.TooltipContent>
  );

  // NOTE: Keyboard accessibility is added with tabIndex, onFocus, onBlur.
  return (
    <Styled.TooltipContainer
      ref={refs.setReference}
      className={className}
      style={style}
      tabIndex={0}
      onMouseEnter={() => !disabled && setOpen(true)}
      onMouseLeave={() => !disabled && setOpen(false)}
      onFocus={() => !disabled && setOpen(true)}
      onBlur={() => !disabled && setOpen(false)}
    >
      {children}
      {open && !disabled && (portal ? createPortal(content, document.body) : content)}
    </Styled.TooltipContainer>
  );
}
